import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { promoOffers } from '@/mocks/promos';

const STORAGE_KEY = 'neonpharma_saved_promos';
const DISMISSED_KEY = 'neonpharma_expiry_banner_dismissed';

const getSavedIds = (): number[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch { /* ignore */ }
  return [];
};

const getDismissedSet = (): Set<string> => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch { /* ignore */ }
  return new Set();
};

const saveDismissedSet = (set: Set<string>) => {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]));
  } catch { /* ignore */ }
};

interface ExpiringPromo {
  id: number;
  title: string;
  color: string;
  hoursLeft: number;
  daysLeft: number;
}

const PromoExpiryBanner = () => {
  const [urgentPromos, setUrgentPromos] = useState<ExpiringPromo[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [visible, setVisible] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    const dismissedSet = getDismissedSet();
    setDismissed(dismissedSet);

    const savedIds = getSavedIds();
    const now = Date.now();

    const expiring: ExpiringPromo[] = [];

    savedIds.forEach((id) => {
      const offer = promoOffers.find((o) => o.id === id);
      if (!offer) return;

      const diff = new Date(offer.endsAt).getTime() - now;
      if (diff <= 0) return;

      const hoursLeft = Math.floor(diff / 3600000);
      const daysLeft = Math.floor(diff / 86400000);

      // Show banner only for promos expiring within 3 days
      if (diff <= 3 * 24 * 60 * 60 * 1000) {
        const bannerKey = `banner_${id}`;
        if (!dismissedSet.has(bannerKey)) {
          expiring.push({ id, title: offer.title, color: offer.color, hoursLeft, daysLeft });
        }
      }
    });

    if (expiring.length > 0) {
      setUrgentPromos(expiring);
      // Delay appearance for smooth UX
      setTimeout(() => setVisible(true), 2500);
    }
  }, []);

  const handleDismiss = (id: number) => {
    const key = `banner_${id}`;
    const newSet = new Set(dismissed);
    newSet.add(key);
    setDismissed(newSet);
    saveDismissedSet(newSet);

    const remaining = urgentPromos.filter((p) => p.id !== id);
    setUrgentPromos(remaining);
    if (remaining.length === 0) setVisible(false);
    else setCurrentIdx((prev) => Math.min(prev, remaining.length - 1));
  };

  const handleDismissAll = () => {
    const newSet = new Set(dismissed);
    urgentPromos.forEach((p) => newSet.add(`banner_${p.id}`));
    saveDismissedSet(newSet);
    setVisible(false);
    setUrgentPromos([]);
  };

  if (!visible || urgentPromos.length === 0) return null;

  const promo = urgentPromos[currentIdx] ?? urgentPromos[0];
  const isUrgent = promo.hoursLeft < 24;

  const timeLabel = isUrgent
    ? `${promo.hoursLeft} ч.`
    : `${promo.daysLeft} дн.`;

  return (
    <div
      className="fixed bottom-6 left-6 z-[75] max-w-sm w-full"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0) scale(1)' : 'translateX(-40px) scale(0.95)',
        transition: 'opacity 0.45s cubic-bezier(0.22,1,0.36,1), transform 0.45s cubic-bezier(0.22,1,0.36,1)',
      }}
    >
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(8,8,14,0.97)',
          border: `1px solid ${promo.color}35`,
          boxShadow: `0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px ${promo.color}10`,
          backdropFilter: 'blur(20px)',
        }}
      >
        {/* Top accent */}
        <div
          className="h-0.5 w-full"
          style={{
            background: `linear-gradient(90deg, ${promo.color}, ${promo.color}40, transparent)`,
          }}
        />

        {/* Animated urgency pulse for critical */}
        {isUrgent && (
          <div
            className="absolute top-0 left-0 right-0 bottom-0 rounded-2xl pointer-events-none"
            style={{
              boxShadow: `0 0 0 0 ${promo.color}`,
              animation: 'promoPulse 2s ease-out infinite',
            }}
          />
        )}

        <div className="p-4">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              {/* Icon */}
              <div
                className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
                style={{
                  background: `${promo.color}15`,
                  border: `1px solid ${promo.color}35`,
                }}
              >
                <i
                  className={isUrgent ? 'ri-alarm-warning-line' : 'ri-alarm-line'}
                  style={{ color: promo.color, fontSize: 18 }}
                />
              </div>
              <div>
                <p
                  className="font-orbitron font-bold text-xs leading-tight"
                  style={{ color: promo.color }}
                >
                  {isUrgent ? '⚡ Акция почти закончилась!' : '⏰ Акция скоро закончится'}
                </p>
                <p className="font-mono text-[10px] text-white/40 mt-0.5">
                  Из ваших закладок
                </p>
              </div>
            </div>

            {/* Close */}
            <button
              onClick={() => handleDismiss(promo.id)}
              className="w-6 h-6 flex items-center justify-center rounded-full cursor-pointer transition-colors shrink-0 mt-0.5"
              style={{ color: 'rgba(255,255,255,0.25)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.7)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.25)'; }}
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>

          {/* Promo info */}
          <div
            className="px-3 py-2.5 rounded-xl mb-3"
            style={{ background: `${promo.color}08`, border: `1px solid ${promo.color}18` }}
          >
            <p className="font-mono text-xs text-white/70 leading-snug line-clamp-2 mb-1.5">
              {promo.title}
            </p>
            <div className="flex items-center gap-2">
              <div
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[10px] font-bold"
                style={{ background: `${promo.color}20`, color: promo.color }}
              >
                <i className="ri-time-line text-[10px]" />
                Осталось: {timeLabel}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Link
              to={`/promos/${promo.id}`}
              onClick={() => handleDismiss(promo.id)}
              className="whitespace-nowrap flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-orbitron font-bold text-xs cursor-pointer transition-all duration-200"
              style={{
                background: promo.color,
                color: '#050508',
                boxShadow: `0 0 14px ${promo.color}40`,
              }}
            >
              <i className="ri-arrow-right-line" />
              Перейти к акции
            </Link>
            {urgentPromos.length > 1 && (
              <button
                onClick={() => setCurrentIdx((prev) => (prev + 1) % urgentPromos.length)}
                className="whitespace-nowrap px-3 py-2.5 rounded-xl font-mono text-xs cursor-pointer transition-all"
                style={{ border: `1px solid ${promo.color}30`, color: promo.color, background: `${promo.color}08` }}
                title="Следующая акция"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            )}
          </div>

          {/* Multiple promos indicator */}
          {urgentPromos.length > 1 && (
            <div className="flex items-center justify-between mt-3">
              <div className="flex gap-1.5">
                {urgentPromos.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIdx(i)}
                    className="cursor-pointer transition-all duration-200 rounded-full"
                    style={{
                      width: i === currentIdx ? 16 : 6,
                      height: 6,
                      background: i === currentIdx ? promo.color : 'rgba(255,255,255,0.15)',
                    }}
                  />
                ))}
              </div>
              <button
                onClick={handleDismissAll}
                className="font-mono text-[10px] cursor-pointer transition-colors"
                style={{ color: 'rgba(255,255,255,0.25)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.25)'; }}
              >
                Скрыть все
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes promoPulse {
          0%   { box-shadow: 0 0 0 0 ${promo.color}40; }
          70%  { box-shadow: 0 0 0 8px ${promo.color}00; }
          100% { box-shadow: 0 0 0 0 ${promo.color}00; }
        }
      `}</style>
    </div>
  );
};

export default PromoExpiryBanner;